/* eslint-disable react/default-props-match-prop-types */
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import classNames from 'classnames'
import { omit } from 'lodash'
import NukaCarousel, { CarouselProps, ControlProps, RenderControlFunctionNames, ScrollMode } from 'nuka-carousel'
import React, { Children, ReactNode } from 'react'
import styles from './Carousel.module.scss'

// In nuka-carousel@5.2.0 CarouselRenderControl type has been renamed to RenderControl and it is no longer exported
declare type RenderControl = ((props: ControlProps) => ReactNode) | null

export enum CarouselModEnum {
  STANDARD = 'standard',
  UPSELL = 'upsell',
}

type Props = CarouselProps & {
  mode: CarouselModEnum | keyof typeof CarouselModEnum
  onIndexChange?: (index: number) => void
  infinite?: boolean
  hideDots?: boolean
}

type NavType = {
  [key in RenderControlFunctionNames]?: RenderControl | null
}

const defaultProps = {
  pauseOnHover: true,
  slidesToShow: 1,
  slidesToScroll: 1,
  scrollMode: ScrollMode.remainder,
  autoplayInterval: 3500,
  speed: 800,
  infinite: true,
  onIndexChange: (idx: number) => {},
}

const Carousel = (_props: Props) => {
  const props = { ...defaultProps, ..._props }
  const nav = {
    renderCenterLeftControls: null,
    renderCenterRightControls: null,
    renderBottomLeftControls: null,
    renderBottomRightControls: null,
    renderTopLeftControls: null,
    renderTopCenterControls: null,
    renderTopRightControls: null,
    renderCenterCenterControls: null,
  } as NavType

  const [disableControls, setDisableControls] = React.useState<boolean>(false)

  const childrenCount = Children.count(props.children)

  if (props.mode === CarouselModEnum.UPSELL) {
    nav.renderBottomCenterControls = ({ previousSlide, currentSlide, nextSlide, slideCount, slidesToShow }) => {
      if (slideCount < slidesToShow) return null
      return (
        <>
          <ChevronLeftIcon
            onClick={() => {
              if (disableControls || (currentSlide === 0 && !props.infinite)) return
              props.onIndexChange!(currentSlide)
              previousSlide()
            }}
            fontSize="large"
            classes={{
              root: classNames(styles['controls__icon'], { [styles['controls__icon--disabled']]: currentSlide === 0 && !props.infinite }),
            }}
          />

          <ChevronRightIcon
            onClick={() => {
              if (disableControls || (currentSlide + slidesToShow === slideCount && !props.infinite)) return
              props.onIndexChange!(currentSlide)
              nextSlide()
            }}
            fontSize="large"
            classes={{
              root: classNames(styles['controls__icon'], {
                [styles['controls__icon--disabled']]: currentSlide + slidesToShow === slideCount && !props.infinite,
              }),
            }}
          />
        </>
      )
    }
  } else {
    nav.renderCenterLeftControls = ({ previousSlide, currentSlide }) => {
      return (currentSlide === 0 && !props.infinite) || childrenCount < 2 ? null : (
        <ChevronLeftIcon
          onClick={() => {
            if (disableControls) return
            props.onIndexChange!(currentSlide)
            previousSlide()
          }}
          fontSize="large"
          classes={{ root: styles['controls__icon'] }}
        />
      )
    }
    nav.renderCenterRightControls = ({ nextSlide, currentSlide, slideCount }) => {
      return (currentSlide === slideCount - 1 && !props.infinite) || childrenCount < 2 ? null : (
        <ChevronRightIcon
          onClick={() => {
            if (disableControls) return
            props.onIndexChange!(currentSlide)
            nextSlide()
          }}
          fontSize="large"
          classes={{ root: styles['controls__icon'] }}
        />
      )
    }

    if (childrenCount < 2 || props.hideDots) nav.renderBottomCenterControls = null
  }
  return (
    <div className={styles['carousel']}>
      <NukaCarousel
        className={classNames({ [styles['carousel--upsell']]: props.mode === CarouselModEnum.UPSELL })}
        defaultControlsConfig={{
          containerClassName: classNames(styles['controls'], {
            [styles['controls--upsell']]: props.mode === CarouselModEnum.UPSELL,
          }),
        }}
        wrapAround={Children.count(props.children) < 2 ? false : props.infinite}
        enableKeyboardControls={false}
        beforeSlide={(currentSlide: number, targetSlide: number) => {
          props.onIndexChange!(targetSlide)
          setDisableControls(true)
        }}
        afterSlide={(idx: number) => {
          setDisableControls(false)
        }}
        {...omit(props, ['mode', 'onIndexChange'])}
        {...nav}
      >
        {props.children}
      </NukaCarousel>
    </div>
  )
}

export default Carousel
